import { RouteFactory } from "@app/routePaths";
import NoAdSettings from "@components/Advertisement/NoAdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import HomepageMasthead from "@components/Header/HomepageMasthead";
import MetaTags from "@components/MetaTags/MetaTags";
import HomeSkybox from "@components/Skybox/HomeSkybox";
import BTGlobalNewsletter from "@pages/Article/components/ArticleNewsletter/BTGlobalNewsletter";
import AseanSection from "@pages/Global/components/AseanSection";
import CompaniesSection from "@pages/Global/components/CompaniesSection";
import EconomyPolicySection from "@pages/Global/components/EconomyPolicySection";
import TechSection from "@pages/Global/components/TechSection";
import TopStoriesSection from "@pages/Global/components/TopStoriesSection";
import { GlobalContext } from "@pages/Global/utils/types";
import { useDetectAdBlock } from "adblock-detect-react";

type GlobalPageContentProps = Pick<GlobalContext, "data">;

/**
 * GlobalPageContent component renders the main content for the Global page.
 * It includes sections for top stories, skybox, economy policy, ASEAN business,
 * companies, tech, and a footer with SEO data.
 * Figma: https://www.figma.com/design/rR8akVWS3SksIyaOSqetu4/BAU--WEB-?node-id=11666-5494&m=dev
 */
const GlobalPageContent = ({
  data: {
    title,
    topStories,
    economyPolicy,
    companies,
    tech,
    seoFooterData,
    aseanBusiness,
    latestNews,
    skyboxArticles,
  },
}: GlobalPageContentProps): React.ReactElement => {
  const adBlockDetected = useDetectAdBlock();

  return (
    <div
      className="w-full text-gray-850"
      data-testid="global-home-content-component"
    >
      <NoAdSettings />

      <Header isFromHome isDisplayEditionSwitcher />

      <HomepageMasthead
        className="pb-4"
        imgWidth={475}
        isDisplayEditionSwitcher
        componentBelowMasthead={
          <p className="pt-2 text-center font-oceanSansStd italic">
            Unpack Asia. Think Global.
          </p>
        }
      />

      <MetaTags
        title={title}
        ogTitle="Global Edition"
        description="Trusted Asia business news & analysis from The Business Times. Get regional perspectives on Southeast Asia & Greater China's markets & economies."
        ogDescription="Trusted Asia business news & analysis from The Business Times. Get regional perspectives on Southeast Asia & Greater China's markets & economies.."
        ogType="website"
        articlePageclass="frontpage"
        slug={RouteFactory.global}
        keywords={[
          "singapore budget 2015",
          "singapore",
          "budget 2015",
          "business & financial news",
        ]}
      />

      <GAData
        title="BT Global Homepage"
        level2="home"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <Container>
        <TopStoriesSection
          articles={topStories}
          linkParams={
            new URLSearchParams({
              ref: "global-top-story",
            })
          }
        />

        <HomeSkybox
          articles={skyboxArticles}
          className="mb-10"
          linkParams={
            new URLSearchParams({
              ref: "global-skybox",
            })
          }
        />

        <BTGlobalNewsletter />

        <EconomyPolicySection
          articles={economyPolicy}
          latestNews={latestNews}
          className="mb-10 border-t border-gray-850 pt-4"
          linkParams={
            new URLSearchParams({
              ref: "global-economy-policy",
            })
          }
        />

        <AseanSection
          articles={aseanBusiness}
          className="mb-10 border-t border-gray-850 pt-4"
          linkParams={
            new URLSearchParams({
              ref: "global-asean-business",
            })
          }
        />

        <CompaniesSection
          articles={companies}
          className="mb-6 border-t border-gray-850 pt-4 lg:mb-10"
          linkParams={
            new URLSearchParams({
              ref: "global-companies",
            })
          }
        />

        <BTGlobalNewsletter />

        <TechSection
          articles={tech}
          className="mb-6 border-t border-gray-850 pt-4 lg:mb-10"
          linkParams={
            new URLSearchParams({
              ref: "global-tech",
            })
          }
        />

        <SeoFooter data={seoFooterData} />
      </Container>
    </div>
  );
};

export default GlobalPageContent;
